import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  CalendarDays,
  Gavel,
  Mail,
  MessageSquare,
  Send,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Certification } from "../../../../../models/certification";
import {
  CertificationStatus,
  DECISION_STATUSES,
  getStatusLabelKey,
  statusRequiresText,
} from "../../../../../models/certificationStatus";
import { AxiosHttpClient } from "../../../../../services/AxiosHttpService";
import { ScrollToBottomContainer } from "../../../../Reusable/ScrollToBottomContainer";
import {
  getUserFullName,
  getUserInitials,
  imageIsDefault,
  useDateFormatting,
} from "../../../../Reusable/Utils";
import { useWebSocket } from "../../../../Reusable/useWebSocket";
import CertificationConversation from "../CertificationConversation";
import { getStatusVisual, StatusBadge } from "../certificationUi";

export default function RespondToCertification() {
  const { certificationId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { formatDateTime } = useDateFormatting();
  const { subscribe } = useWebSocket();
  const [certification, setCertification] = useState<Certification | null>(
    null
  );
  const [inputMessage, setInputMessage] = useState("");
  const [decision, setDecision] = useState<CertificationStatus | "">("");
  const [finalDecision, setFinalDecision] = useState("");

  const getCertification = () => {
    AxiosHttpClient.get<Certification>(
      "api/v1/docuveritas/conversations/messages/certification/" +
        certificationId
    )
      .then(setCertification)
      .catch(console.error);
  };

  const resolveCertification = () => {
    if (!decision) return;
    if (statusRequiresText(decision) && !finalDecision.trim()) return;
    AxiosHttpClient.post(
      "api/v1/docuveritas/conversations/messages/certifications/response/" +
        certificationId,
      {
        status: decision,
        finalDecision: finalDecision,
      }
    )
      .then(() => {
        navigate("/feature/certifications");
      })
      .catch((err) => {});
  };

  const addMessage = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!inputMessage.trim()) return;
    AxiosHttpClient.post(
      "api/v1/docuveritas/conversations/messages/certifications/message",
      {
        content: inputMessage,
        certificationId: certificationId,
      }
    )
      .then(() => {
        setInputMessage("");
        getCertification();
      })
      .catch((err) => {});
  };

  useEffect(() => {
    getCertification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certificationId]);

  useEffect(() => {
    if (!certification) return;
    const sub = subscribe(
      `/topic/${certification.id}/certificationMessages`,
      () => getCertification()
    );
    return () => sub.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certification, subscribe]);

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Back header */}
      <div className="mb-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/feature/certifications")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-semibold tracking-tight">
            {t("RESPOND_CERTIFICATION_BACK_LABEL")}
          </h1>
        </div>
        {certification && <StatusBadge status={certification.status} />}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left column: context + conversation */}
        <div className="space-y-6 lg:col-span-2">
          {/* Question context */}
          <Card>
            <CardContent className="space-y-4 p-6">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="font-medium">
                  {certification?.category}
                </Badge>
                <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                  <CalendarDays className="h-3.5 w-3.5" />
                  {formatDateTime(certification?.requestedAt)}
                </span>
              </div>

              <h2 className="text-lg font-semibold leading-snug">
                {certification?.chatMessage.question?.content}
              </h2>

              <Separator />

              <div className="flex items-center gap-3">
                <Avatar className="h-11 w-11">
                  <AvatarImage
                    src={
                      imageIsDefault(certification?.requestedBy?.imageUrl)
                        ? undefined
                        : certification?.requestedBy?.imageUrl
                    }
                    alt={getUserFullName(certification?.requestedBy)}
                  />
                  <AvatarFallback>
                    {getUserInitials(certification?.requestedBy)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">
                    {t("RESPOND_CERTIFICATION_ASKED_BY")}
                  </p>
                  <p className="truncate font-medium">
                    {getUserFullName(certification?.requestedBy)}
                  </p>
                  <p className="inline-flex items-center gap-1 truncate text-xs text-muted-foreground">
                    <Mail className="h-3 w-3 shrink-0" />
                    {certification?.requestedBy.email}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Conversation */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                {t("RESPOND_CERTIFICATION_CONVERSATION")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <ScrollToBottomContainer
                sx={{ maxHeight: "50vh", overflowY: "auto" }}
              >
                <CertificationConversation certification={certification} />
              </ScrollToBottomContainer>

              <form
                onSubmit={addMessage}
                className="flex items-end gap-2 rounded-xl border bg-background p-2"
              >
                <Textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      addMessage();
                    }
                  }}
                  placeholder={t("RESPOND_CERTIFICATION_MESSAGE_PLACEHOLDER")}
                  className="max-h-40 min-h-[44px] resize-none border-0 bg-transparent shadow-none focus-visible:ring-0"
                />
                <Button
                  type="submit"
                  size="icon"
                  className="shrink-0"
                  disabled={!inputMessage.trim()}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Right column: decision */}
        <div className="lg:col-span-1">
          <Card className="lg:sticky lg:top-6">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Gavel className="h-4 w-4 text-muted-foreground" />
                {t("RESPOND_CERTIFICATION_ACTIONS_TITLE")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t("RESPOND_CERTIFICATION_DECISION_LABEL")}</Label>
                <Select
                  value={decision || undefined}
                  onValueChange={(v) => setDecision(v as CertificationStatus)}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t("RESPOND_CERTIFICATION_DECISION_LABEL")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {DECISION_STATUSES.map((s) => {
                      const Icon = getStatusVisual(s).icon;
                      return (
                        <SelectItem key={s} value={s}>
                          <span className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            {t(getStatusLabelKey(s))}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {decision && (
                <div className="space-y-2">
                  <Label>{t(`RESPOND_CERTIFICATION_FINAL_${decision}`)}</Label>
                  <Textarea
                    rows={6}
                    value={finalDecision}
                    onChange={(e) => setFinalDecision(e.target.value)}
                    placeholder={t(`RESPOND_CERTIFICATION_FINAL_${decision}`)}
                    className="resize-none"
                  />
                </div>
              )}

              <Button
                className="w-full"
                disabled={
                  !decision ||
                  (statusRequiresText(decision as CertificationStatus) &&
                    !finalDecision.trim())
                }
                onClick={resolveCertification}
              >
                <Gavel className="h-4 w-4" />
                {t("RESPOND_CERTIFICATION_DECISION_BUTTON")}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
